import { Component, type OnInit, input } from '@angular/core';
import {
  ConsumptionValuesVM,
  CostPerUnitVM,
  Offer,
} from '@innogy/become-a-customer/shared/interfaces';
import { InnogyComponentRendering } from '@core/jss-models';
import { ComponentFields, JssModule } from '@sitecore-jss/sitecore-jss-angular';
import {
  GenericRichTextDirective,
  getFieldValue,
  TextInterpolationPipe,
} from '@core/jss-utils';
import { CSCaptureDirective } from '@legacy/utils-contentsquare';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { RoundedCurrencyPipe } from '@innogy/utils-deprecated';
import {
  RichTextWrapperComponent,
  ToolTipComponent,
} from '@legacy/common-ui-utility-components';
import { offerHasDiscount } from '@innogy/become-a-customer/shared';

import { MonthlyBudgetPaymentSection } from './aom-content.model';
import { AomCostsPerUnitComponent } from './aom-costs-per-unit/aom-costs-per-unit.component';
import { DisclaimerComponent } from './disclaimer/disclaimer.component';
import { AomCostTableComponent } from './aom-cost-table/aom-cost-table.component';

@Component({
  selector: 'wl-aom-content',
  templateUrl: './aom-content.component.html',
  styleUrls: [
    './aom-content.component.ed.scss',
    './aom-content.component.essent.scss',
  ],
  standalone: true,
  imports: [
    JssModule,
    CommonModule,
    RichTextWrapperComponent,
    AomCostsPerUnitComponent,
    AomCostTableComponent,
    DisclaimerComponent,
    AutomationIdDirective,
    GenericRichTextDirective,
    CSCaptureDirective,
    TextInterpolationPipe,
    RoundedCurrencyPipe,
    ToolTipComponent,
    CurrencyPipe,
  ],
})
export class AomContentComponent implements OnInit {
  readonly consumptionValuesVM = input<ConsumptionValuesVM>();
  readonly costPerUnitVM = input<CostPerUnitVM>();
  readonly fields = input.required<ComponentFields>();
  readonly isVATIncluded = input(false);
  readonly offer = input<Offer>();
  readonly rendering = input<InnogyComponentRendering>();
  readonly enableRemoveBBA = input<boolean | undefined | null>(false);
  readonly getFieldValue = getFieldValue;

  monthlyBudgetPaymentSection!: MonthlyBudgetPaymentSection;

  get kwhNormal() {
    return this.consumptionValuesVM()?.electricity.supplyNormal;
  }

  get kwhLow() {
    return this.consumptionValuesVM()?.electricity.supplyLow;
  }

  get gas() {
    return this.consumptionValuesVM()?.gas;
  }

  get solarReturn() {
    return this.consumptionValuesVM()?.electricity.returnSupplyNormal;
  }

  get solarReturnLow() {
    return this.consumptionValuesVM()?.electricity.returnSupplyLow;
  }

  get budgetBillAmount() {
    return this.offer()?.budgetBillAmount || 0;
  }

  get hasDiscount() {
    return offerHasDiscount(this.offer());
  }

  get disclaimer() {
    return this.offer()?.disclaimerContent ?? '';
  }

  get isDynamicProduct() {
    return !!this.offer()?.isDynamicProduct;
  }

  get monthlyBudgetPaymentHeading(): string {
    return this.isDynamicProduct
      ? this.monthlyBudgetPaymentSection.dynamicMonthlyBudgetPaymentHeading
      : this.monthlyBudgetPaymentSection.fixedMonthlyBudgetPaymentHeading;
  }

  get monthlyBudgetPaymentParagraph(): string {
    return this.isDynamicProduct
      ? this.monthlyBudgetPaymentSection.dynamicMonthlyBudgetPaymentParagraph
      : this.monthlyBudgetPaymentSection.fixedMonthlyBudgetPaymentParagraph;
  }

  ngOnInit(): void {
    this.monthlyBudgetPaymentSection = new MonthlyBudgetPaymentSection(
      this.fields()
    );
  }
}
